import { useCallback, useEffect, useState } from 'react';
import { ArrowBack, SearchRounded } from '@mui/icons-material';
import { Box, Button, CircularProgress, SwipeableDrawer } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  Container,
  Header,
  TextField,
  Div,
  TabSelection,
  TabButton,
} from './styles';

import { ListagemContas } from './ListagemContas';
import { ListagemEmpresas } from './ListagemEmpresas';
import { DrawerCriarConta } from './DrawerCriarConta';

import useAuthStore from '../../store/auth';

import {
  Account,
  Company,
  getAccountByUserIdRequest,
  getCompaniesByUserIdRequest,
  getCompaniesRequest,
  getUserByIdRequest,
  User,
} from '../../services';

export const Contas = () => {
  const navigate = useNavigate();

  const user = useAuthStore(state => state.user);
  const setMenuSelected = useAuthStore(state => state.setMenuSelected);

  const [listagemContas, setListagemContas] = useState<Account[]>([]);
  const [listagemEmpresas, setListagemEmpresas] = useState<Company[]>([]);
  const [tabSelected, setTabSelected] = useState('contas');
  const [openDrawerCriarConta, setOpenDrawerCriarConta] = useState(false);
  const [searchForAccount, setSearchForAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [pageAccount, setPageAccount] = useState(0);
  const [sizeAccount, setSizeAccount] = useState(10);
  const [pageCompanies, setPageCompanies] = useState(0);
  const [sizeCompanies, setSizeCompanies] = useState(10);
  const [countAccounts, setCountAccounts] = useState<number>();
  const [countCompanies, setCountCompanies] = useState<number>();

  const [userState, setUserState] = useState({} as User);

  const loadUserById = useCallback(async () => {
    if (user?.id) {
      const response = await getUserByIdRequest(user?.id);

      if (response?.status === 200) {
        setUserState(response?.data);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      loadUserById();
    }
  }, [user]);

  const loadAccount = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      if (user?.profile === 'UN1' || user?.profile === 'UN2') {
        const response = await getAccountByUserIdRequest(
          user.id,
          searchForAccount,
          pageAccount.toString(),
          sizeAccount.toString(),
        );
        // console.log({ response });
        setListagemContas(response?.data.accounts);
        setCountAccounts(response?.data.accountsCounts);
      } else {
        setListagemContas([]);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingAccounts(false);
    }
  }, [pageAccount, searchForAccount, sizeAccount, user]);

  useEffect(() => {
    setLoadingAccounts(true);
    const delayDebounceFn = setTimeout(() => {
      loadAccount();
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [searchForAccount, sizeAccount, pageAccount]);

  async function loadCompanies() {
    try {
      setLoadingCompanies(true);
      if (user?.profile === 'UN1') {
        const response = await getCompaniesRequest(
          searchForAccount,
          pageCompanies?.toString(),
          sizeCompanies?.toString(),
        );

        if (response?.status === 200) {
          setListagemEmpresas(response?.data.companies);
          setCountCompanies(response?.data.companiesCount);
        }
      } else {
        const response = await getCompaniesByUserIdRequest(
          user?.id as string,
          searchForAccount,
          pageCompanies?.toString(),
          sizeCompanies?.toString(),
        );

        if (response?.status === 200) {
          setListagemEmpresas(response?.data.companies);
          setCountCompanies(response?.data.companiesCount);
        }
      }
    } catch (err) {
      console.error('loadCompanies => ', err);
    } finally {
      setLoadingCompanies(false);
    }
  }

  useEffect(() => {
    setLoadingCompanies(true);
    const delayDebounceFn = setTimeout(() => {
      loadCompanies();
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [searchForAccount, pageCompanies, sizeCompanies]);

  const handleChangeToAccount = () => {
    setTabSelected('contas');
    setSearchForAccount('');
  };

  const handleChangeToCompany = () => {
    setTabSelected('empresas');
    setSearchForAccount('');
  };

  const closeDrawer = useCallback(() => {
    setOpenDrawerCriarConta(false);
  }, []);

  const handleGoBack = () => {
    setMenuSelected('portal');
    navigate('/home/reunioes');
  };

  return (
    <Container>
      {user?.companies_ids && user.companies_ids.length > 0 && (
        <Button
          onClick={handleGoBack}
          sx={{
            display: 'flex',
            gap: '10px',
            color: '#212121',
            borderColor: '#212121',
            width: '213px',
            height: '48px',
            fontWeight: '500',
            fontSize: '15px',
            whiteSpace: 'nowrap',
            marginBottom: '20px',
          }}
          variant="outlined"
        >
          <ArrowBack /> Voltar ao portal
        </Button>
      )}
      <Header>
        <TabSelection>
          {(userState?.profile === 'UN1' || userState?.profile === 'UN2') && (
            <TabButton
              active={tabSelected === 'contas'}
              onClick={handleChangeToAccount}
            >
              CONTAS
            </TabButton>
          )}
          {userState?.profile === 'UN1' && (
            <TabButton
              active={tabSelected === 'empresas'}
              onClick={handleChangeToCompany}
            >
              EMPRESAS
            </TabButton>
          )}
        </TabSelection>
        <TextField
          fullWidth
          placeholder={
            tabSelected === 'contas' ? 'Pesquisar Conta' : 'Pesquisar Empresa'
          }
          variant="standard"
          value={searchForAccount}
          onChange={ev => setSearchForAccount(ev.target.value)}
          sx={{
            background: '#FFFFFF',
            borderRadius: '4px',
            padding: '9px 12px',
          }}
          InputProps={{
            endAdornment: <SearchRounded />,
            disableUnderline: true, //
          }}
        />
        {userState?.profile === 'UN1' ? (
          <Button
            variant="contained"
            sx={{
              minWidth: '165px',
              width: '165px',
              height: '48px',
              padding: '6px 22px',
              fontWeight: '500',
              lineHeight: '14px',
              whiteSpace: 'nowrap',
            }}
            onClick={() => setOpenDrawerCriarConta(true)}
          >
            Criar Conta
          </Button>
        ) : (
          ''
        )}
      </Header>
      <Div sx={{ border: 0 }}>
        {tabSelected === 'contas' ? (
          loadingAccounts ? (
            <Box width="100%" display="flex" justifyContent="center" mt="200px">
              <CircularProgress />
            </Box>
          ) : (
            <ListagemContas
              listagemContas={listagemContas}
              pageAccount={pageAccount}
              sizeAccount={sizeAccount}
              countAccounts={countAccounts}
              setPageAccount={setPageAccount}
              setSizeAccount={setSizeAccount}
            />
          )
        ) : loadingCompanies ? (
          <Box width="100%" display="flex" justifyContent="center" mt="200px">
            <CircularProgress />
          </Box>
        ) : (
          <ListagemEmpresas
            listagemEmpresas={listagemEmpresas}
            pageCompanies={pageCompanies}
            sizeCompanies={sizeCompanies}
            setPageCompanies={setPageCompanies}
            setSizeCompanies={setSizeCompanies}
            countCompanies={countCompanies}
          />
        )}
      </Div>
      <SwipeableDrawer
        anchor="right"
        open={openDrawerCriarConta}
        onClose={() => setOpenDrawerCriarConta(false)}
        onOpen={() => setOpenDrawerCriarConta(true)}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <DrawerCriarConta closeDrawer={closeDrawer} />
      </SwipeableDrawer>
    </Container>
  );
};
